/**
 * A PDF's pages as JPEG images, rendered in the browser so the server does not have to.
 *
 * The server used to rasterise every uploaded PDF with pdftoppm, and one heavy PDF held three web
 * instances at 100% CPU for half an hour (tasks/browser-pdf-pages.md). pdf.js parses the file in its
 * own worker; each page is drawn here on a canvas, one at a time, yielding between pages so the
 * screen stays responsive. Ported from the patient app's `core/pdf-pages.ts`.
 *
 * Pages are drawn with the 'print' intent: the default 'display' intent paces drawing with
 * requestAnimationFrame, which a hidden tab never fires, so switching tabs mid-upload stalled it.
 *
 * pdf.js is loaded on first use, so it costs nothing until someone attaches a PDF.
 */

export const PDF_MAX_PAGES = 300;
export const PAGE_MAX_SIDE_PX = 3000;
export const PAGE_QUALITY = 0.85;

/** The scale for a page whose size at scale 1 (PDF points, 72 to the inch) is width x height. */
export function pageScale(width: number, height: number, dpi = 150): number {
  const dpiScale = dpi / 72;
  const longSide = Math.max(width, height);
  if (!(longSide > 0)) return dpiScale;
  return Math.min(dpiScale, PAGE_MAX_SIDE_PX / longSide);
}

let pdfjsLoading: Promise<typeof import('pdfjs-dist')> | null = null;

function loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
  pdfjsLoading ??= import('pdfjs-dist').then((pdfjs) => {
    // Copied into assets at build time (angular.json), so it comes from our own origin.
    pdfjs.GlobalWorkerOptions.workerSrc = new URL('assets/pdfjs/pdf.worker.min.js', document.baseURI).href;
    return pdfjs;
  });
  return pdfjsLoading;
}

export interface RenderPdfPagesOptions {
  dpi?: number;
  /** Called with (pages done, page count) once the PDF is open and after each page. */
  onPage?: (done: number, total: number) => void;
}

/** Every page of `file` as a JPEG, in order. Rejects when the PDF cannot be read or is too long. */
export async function renderPdfPages(file: Blob, { dpi = 150, onPage }: RenderPdfPagesOptions = {}): Promise<Blob[]> {
  const pdfjs = await loadPdfjs();
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  try {
    const doc = await task.promise;
    if (doc.numPages > PDF_MAX_PAGES) throw new Error(`PDF has ${doc.numPages} pages`);
    onPage?.(0, doc.numPages);
    const pages: Blob[] = [];
    for (let n = 1; n <= doc.numPages; n += 1) {
      const page = await doc.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: pageScale(base.width, base.height, dpi) });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('No canvas');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: context, viewport, intent: 'print' }).promise;
      pages.push(await toJpeg(canvas));
      page.cleanup();
      canvas.width = 0;
      canvas.height = 0;
      onPage?.(n, doc.numPages);
      await nextTask();
    }
    return pages;
  } finally {
    await task.destroy();
  }
}

/** Lets the page breathe between pages. A message, unlike setTimeout, is not slowed in a hidden tab. */
function nextTask(): Promise<void> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => {
      channel.port1.close();
      resolve();
    };
    channel.port2.postMessage(null);
  });
}

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Page could not be encoded'))), 'image/jpeg', PAGE_QUALITY));
}
